import React, { useState } from 'react';
import { Plus, Calendar, Clock, MapPin, User, Phone, Brain, Smartphone, Search, Filter, CheckCircle, XCircle, AlertCircle, Video, Mail, Edit, Trash2, Eye } from 'lucide-react';
import { useExampleData } from '../data/example/ExampleDataProvider';
import { exampleAppointments, Appointment } from '../data/example/appointments';
import { MockBadge } from './common/MockBadge';



interface Provider {
  id: string;
  name: string;
  specialty: string;
  location: string;
  availableSlots: string[];
  rating: number;
  image?: string;
}

export const AppointmentScheduler: React.FC = () => {
  const { isExampleDataEnabled } = useExampleData();
  const [appointments, setAppointments] = useState<Appointment[]>([]);

  // Load appointments from centralized example data when enabled
  React.useEffect(() => {
    if (isExampleDataEnabled) {
      setAppointments(exampleAppointments);
    }
  }, [isExampleDataEnabled]);

  const [providers] = useState<Provider[]>([
    {
      id: 'provider-1',
      name: 'Dr. Sarah Smith',
      specialty: 'Endocrinology',
      location: 'Main Hospital',
      availableSlots: ['09:00', '10:00', '14:00', '15:00'],
      rating: 4.8
    },
    {
      id: 'provider-2',
      name: 'Dr. Michael Johnson',
      specialty: 'Cardiology',
      location: 'Cardiology Clinic',
      availableSlots: ['08:30', '10:30', '13:30', '16:30'],
      rating: 4.6
    },
    {
      id: 'provider-3',
      name: 'Dr. Emily Davis',
      specialty: 'Primary Care',
      location: 'Virtual',
      availableSlots: ['09:00', '11:00', '14:00', '16:00'],
      rating: 4.9
    },
    {
      id: 'provider-4',
      name: 'Dr. Robert Wilson',
      specialty: 'Family Medicine',
      location: 'Family Health Center',
      availableSlots: ['08:00', '09:00', '10:00', '11:00'],
      rating: 4.7
    }
  ]);

  const [activeTab, setActiveTab] = useState<'upcoming' | 'past' | 'schedule' | 'providers'>('upcoming');
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'scheduled' | 'confirmed' | 'completed' | 'cancelled'>('all');
  const [filterType, setFilterType] = useState<'all' | 'in-person' | 'telemedicine' | 'phone'>('all');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'text-blue-600 bg-blue-50';
      case 'confirmed': return 'text-green-600 bg-green-50';
      case 'completed': return 'text-gray-600 bg-gray-50';
      case 'cancelled': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'in-person': return MapPin;
      case 'telemedicine': return Video;
      case 'phone': return Phone;
      default: return User;
    }
  };

  const upcomingAppointments = appointments.filter(apt => 
    new Date(apt.date) >= new Date() && apt.status !== 'cancelled'
  );

  const pastAppointments = appointments.filter(apt => 
    new Date(apt.date) < new Date() || apt.status === 'cancelled'
  );

  const filteredAppointments = appointments.filter(apt => {
    const matchesSearch = apt.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         apt.doctor.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         apt.specialty.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || apt.status === filterStatus;
    const matchesType = filterType === 'all' || apt.type === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  const handleScheduleAppointment = (provider: Provider, date: string, time: string) => {
    const newAppointment: Appointment = {
      id: Date.now().toString(),
      title: 'New Appointment',
      doctor: provider.name,
      specialty: provider.specialty,
      date,
      time,
      location: provider.location,
      type: 'in-person',
      status: 'scheduled',
      duration: 30,
      providerId: provider.id,
      patientId: 'patient-1',
      reminderSent: false,
      dataExportPrepared: false
    };
    setAppointments([...appointments, newAppointment]);
    setShowScheduleModal(false);
  };

  const handleCancelAppointment = (appointmentId: string) => {
    setAppointments(appointments.map(apt => 
      apt.id === appointmentId ? { ...apt, status: 'cancelled' } : apt
    ));
  };

  const handlePrepareDataExport = (appointmentId: string) => {
    setAppointments(appointments.map(apt => 
      apt.id === appointmentId ? { ...apt, dataExportPrepared: true } : apt
    ));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <img 
              src="/zippy-logo-simple.svg" 
              alt="ZippyHealth Logo"
              className="w-8 h-8"
            />
            <div className="flex items-center gap-2">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
                Appointment Scheduler
              </h2>
              {isExampleDataEnabled && <MockBadge />}
            </div>
          </div>
          <p className="text-slate-600 mt-1">Manage healthcare appointments with ZippyHealth.io data integration</p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => setShowScheduleModal(true)}
            className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all transform hover:scale-105"
          >
            <Plus className="h-4 w-4" />
            <span>Schedule Appointment</span>
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'upcoming', label: 'Upcoming', count: upcomingAppointments.length },
            { id: 'past', label: 'Past', count: pastAppointments.length },
            { id: 'schedule', label: 'Schedule New', count: 0 },
            { id: 'providers', label: 'Providers', count: providers.length }
          ].map(({ id, label, count }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id as any)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === id
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>{label}</span>
              {count > 0 && (
                <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                  {count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[600px]">
        {(activeTab === 'upcoming' || activeTab === 'past') && (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search appointments..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="scheduled">Scheduled</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as any)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="in-person">In-Person</option>
                <option value="telemedicine">Telemedicine</option>
                <option value="phone">Phone</option>
              </select>
            </div>

            {/* Appointments List */}
            <div className="space-y-4">
              {(activeTab === 'upcoming' ? upcomingAppointments : pastAppointments)
                .filter(apt => {
                  const matchesSearch = apt.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                       apt.doctor.toLowerCase().includes(searchQuery.toLowerCase());
                  const matchesStatus = filterStatus === 'all' || apt.status === filterStatus;
                  const matchesType = filterType === 'all' || apt.type === filterType;
                  return matchesSearch && matchesStatus && matchesType;
                })
                .map((appointment) => {
                  const TypeIcon = getTypeIcon(appointment.type);
                  return (
                    <div key={appointment.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                              <TypeIcon className="h-6 w-6 text-red-600" />
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h3 className="text-lg font-semibold text-gray-900">{appointment.title}</h3>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                                {appointment.status}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">{appointment.doctor} • {appointment.specialty}</p>
                            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                              <div className="flex items-center space-x-1">
                                <Calendar className="h-4 w-4" />
                                <span>{new Date(appointment.date).toLocaleDateString()}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Clock className="h-4 w-4" />
                                <span>{appointment.time} ({appointment.duration} min)</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <MapPin className="h-4 w-4" />
                                <span>{appointment.location}</span>
                              </div>
                            </div>
                            {appointment.notes && (
                              <p className="text-sm text-gray-600 mt-2">{appointment.notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {appointment.status === 'scheduled' && (
                            <>
                              <button 
                                onClick={() => handlePrepareDataExport(appointment.id)}
                                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                  appointment.dataExportPrepared 
                                    ? 'bg-green-50 text-green-700' 
                                    : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                                }`}
                              >
                                <Brain className="h-4 w-4 inline mr-1" />
                                {appointment.dataExportPrepared ? 'Data Ready' : 'Prep Data'}
                              </button>
                              <button 
                                onClick={() => handleCancelAppointment(appointment.id)}
                                className="px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors"
                              >
                                Cancel
                              </button>
                            </>
                          )}
                          {appointment.type === 'telemedicine' && appointment.status === 'confirmed' && (
                            <button className="px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors">
                              Join Call
                            </button>
                          )}
                          <button className="px-3 py-2 bg-gray-50 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors">
                            <Eye className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {activeTab === 'schedule' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Schedule New Appointment</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Provider</label>
                  <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent">
                    <option>Choose a provider...</option>
                    {providers.map(provider => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name} - {provider.specialty}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Appointment Type</label>
                  <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent">
                    <option value="in-person">In-Person</option>
                    <option value="telemedicine">Telemedicine</option>
                    <option value="phone">Phone</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                  <input 
                    type="date" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                  <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent">
                    <option>Select time...</option>
                    <option>09:00</option>
                    <option>10:00</option>
                    <option>11:00</option>
                    <option>14:00</option>
                    <option>15:00</option>
                    <option>16:00</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea 
                    rows={3}
                    placeholder="Any special notes or requirements..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                  Schedule Appointment
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'providers' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {providers.map((provider) => (
                <div key={provider.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <User className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{provider.name}</h3>
                      <p className="text-sm text-gray-600">{provider.specialty}</p>
                      <div className="flex items-center space-x-1 mt-1">
                        <span className="text-yellow-500">★</span>
                        <span className="text-sm text-gray-600">{provider.rating}</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>{provider.location}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Available Times</p>
                      <div className="flex flex-wrap gap-1">
                        {provider.availableSlots.map((slot, index) => (
                          <span key={index} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                            {slot}
                          </span>
                        ))}
                      </div>
                    </div>
                    <button className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                      Schedule with {provider.name}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Schedule Appointment</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
                <select className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                  {providers.map(provider => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name} - {provider.specialty}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <input type="date" className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                <select className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                  <option>09:00</option>
                  <option>10:00</option>
                  <option>11:00</option>
                  <option>14:00</option>
                  <option>15:00</option>
                  <option>16:00</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end space-x-3 mt-6">
              <button 
                onClick={() => setShowScheduleModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                Schedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
